import type L from 'leaflet';
import { useUIStore, selectRightDockInset, selectLeftDockInset } from '../../store/uiStore';
import { getLeaflet } from './leafletHelpers';

/**
 * Leaflet fitBounds padding for every plan fit (the whole plan from a link
 * or "Show whole plan", and a single feature from a list row), so the fitted
 * shape lands in the map that's actually visible: clear of a docked panel on
 * either side and of the phone plan card under the search bar. The plan
 * editor/viewer dock on the LEFT (Sept 2026), so the left side needs the
 * same padding the right side always got — a plan or feature fit into the
 * strip under a left-docked panel is the same bug a right-docked one used to
 * cause. Read from the store at call time rather than a hook, because both
 * callers fit from effects and imperative handles.
 */
export function planFitPadding(basePx = 64): {
  paddingTopLeft: [number, number];
  paddingBottomRight: [number, number];
} {
  const s = useUIStore.getState();
  return {
    paddingTopLeft: [basePx + selectLeftDockInset(s), basePx + s.mapTopInset],
    paddingBottomRight: [basePx + selectRightDockInset(s), basePx],
  };
}

/**
 * True when `bounds` sits wholly inside the part of the map that `padding`
 * leaves clear (the viewport minus docked panels and top chrome). A plan
 * that is on screen but under the plan panel, or cut by its edge, is not in
 * clear view.
 */
export function boundsInClearView(
  map: L.Map,
  bounds: L.LatLngBoundsExpression,
  padding: ReturnType<typeof planFitPadding>
): boolean {
  const L = getLeaflet();
  const box = L.latLngBounds(bounds as L.LatLngBoundsLiteral);
  const size = map.getSize();
  const nw = map.latLngToContainerPoint(box.getNorthWest());
  const se = map.latLngToContainerPoint(box.getSouthEast());
  const [left, top] = padding.paddingTopLeft;
  const [right, bottom] = padding.paddingBottomRight;
  return nw.x >= left && nw.y >= top && se.x <= size.x - right && se.y <= size.y - bottom;
}
